import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { useIntl } from 'react-intl';
import { PageTitle, PeriodFilterControls, type PeriodFilterDateRange } from 'components';
import { PublicOutlined } from '@mui/icons-material';
import { FormHelperText, LinearProgress, Stack, Typography } from '@mui/material';
import { TimeRange } from 'modules/home/components/DropdownFilter';
import { getStartOfCurrentFiscalYear } from 'modules/reports/utils';
import { useGetSettingsQuery } from 'gql';
import { ActivityType } from 'gql';
import { ActivitiesSection } from '../components/ActivitiesSection';
import { IndividualsSection } from '../components/IndividualsSection';
import { SummarySection } from '../components/SummarySection';

type GlobalStatisticsReport = {
  startDate: string
  endDate: string
  totalOrganizations: number
  totalActivities: number
  totalIndividuals: number
  totalItemsDistributed: number
  activitiesByType: { activityType: ActivityType, count: number }[]
  activitiesByTheme: { themeId: number, themeName: string, count: number }[]
  individualsByAgeGroup: { minAge: number, maxAge?: number | null, count: number }[]
}

const defaultCustomRange = () => ({
  startDate: dayjs().subtract(7, 'day').startOf('day').toISOString(),
  endDate: dayjs().endOf('day').toISOString()
});

const formatAgeGroupLabel = (formatMessage: ReturnType<typeof useIntl>['formatMessage']) =>
  (minAge: number, maxAge?: number | null) => {
    if (maxAge == null) {
      return formatMessage({ id: '{minAge}+ years' }, { minAge });
    }
    return formatMessage({ id: '{minAge} to {maxAge} years' }, { minAge, maxAge });
  };

const getPreviousDateRange = (range: PeriodFilterDateRange, timeRange: TimeRange): PeriodFilterDateRange => {
  const startDate = dayjs(range.startDate);
  const endDate = dayjs(range.endDate);

  if (timeRange === TimeRange.beginFiscalYear) {
    return {
      startDate: startDate.subtract(1, 'year').startOf('day').toISOString(),
      endDate: endDate.subtract(1, 'year').endOf('day').toISOString()
    };
  }

  const periodInDays = endDate.diff(startDate, 'day');
  return {
    startDate: startDate.subtract(periodInDays, 'day').startOf('day').toISOString(),
    endDate: startDate.toISOString()
  };
};

export const GlobalReportPage: React.FC = () => {
  const { formatMessage } = useIntl();
  const { data: settingsData } = useGetSettingsQuery();
  const fiscalYearStart = settingsData?.settings.at(0)?.fiscalYearStart;

  const [timeRangeFilter, setTimeRangeFilter] = useState(TimeRange.last7Days);
  const [customDateRange, setCustomDateRange] = useState<PeriodFilterDateRange>(defaultCustomRange);
  const [reportData, setReportData] = useState<GlobalStatisticsReport | null>(null);
  const [previousReportData, setPreviousReportData] = useState<GlobalStatisticsReport | null>(null);
  const [isLoading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const dateRange = useMemo(() => {
    let startDate = dayjs().startOf('day');
    let endDate = dayjs().endOf('day');

    switch (timeRangeFilter) {
      case TimeRange.last7Days:
        startDate = dayjs().subtract(7, 'day').startOf('day');
        break;
      case TimeRange.lastMonth:
        startDate = dayjs().subtract(1, 'month').startOf('day');
        break;
      case TimeRange.beginFiscalYear:
        startDate = fiscalYearStart
          ? getStartOfCurrentFiscalYear(fiscalYearStart).startOf('day')
          : dayjs().startOf('year');
        break;
      case TimeRange.customRange: {
        const customStart = customDateRange.startDate ? dayjs(customDateRange.startDate) : null;
        const customEnd = customDateRange.endDate ? dayjs(customDateRange.endDate) : null;
        if (!customStart?.isValid() || !customEnd?.isValid()) {
          return null;
        }
        startDate = customStart.startOf('day');
        endDate = customEnd.endOf('day');
        break;
      }
    }

    return {
      startDate: startDate.local().startOf('day').toISOString(),
      endDate: endDate.local().endOf('day').toISOString()
    };
  }, [customDateRange.endDate, customDateRange.startDate, fiscalYearStart, timeRangeFilter]);

  useEffect(() => {
    if (!dateRange) {
      setReportData(null);
      setPreviousReportData(null);
      return;
    }

    const controller = new AbortController();

    const fetchReport = async (range: PeriodFilterDateRange) => {
      const response = await fetch('api/Reporting/GlobalStatistics', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Accept-Language': dayjs.locale()
        },
        signal: controller.signal,
        body: JSON.stringify(range)
      });

      if (!response.ok) {
        throw new Error('Failed to fetch global statistics');
      }

      return await response.json() as GlobalStatisticsReport;
    };

    const fetchReports = async () => {
      setLoading(true);
      setHasError(false);
      setPreviousReportData(null);
      try {
        const previousRange = getPreviousDateRange(dateRange, timeRangeFilter);
        const [currentResult, previousResult] = await Promise.allSettled([
          fetchReport(dateRange),
          fetchReport(previousRange)
        ]);

        if (!controller.signal.aborted) {
          if (currentResult.status === 'fulfilled') {
            setReportData(currentResult.value);
          } else {
            setHasError(true);
            setReportData(null);
          }

          if (previousResult.status === 'fulfilled') {
            setPreviousReportData(previousResult.value);
          } else {
            setPreviousReportData(null);
          }
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          setHasError(true);
          setReportData(null);
          setPreviousReportData(null);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchReports();
    return () => controller.abort();
  }, [dateRange, timeRangeFilter]);

  const renderEmptyState = () => (
    <Typography variant='body2' color='text.secondary'>
      {formatMessage({ id: 'No data available for this period.' })}
    </Typography>
  );

  return (
    <>
      <PageTitle
        icon={<PublicOutlined />}
        title={formatMessage({ id: 'Global report' })}
        actionButton={(
          <PeriodFilterControls
            timeRange={timeRangeFilter}
            onTimeRangeChange={setTimeRangeFilter}
            customDateRange={customDateRange}
            onCustomDateRangeChange={updates => setCustomDateRange(prev => ({ ...prev, ...updates }))}
          />
        )}
      />

      <Stack gap={4}>
        {isLoading && <LinearProgress />}
        {hasError && <FormHelperText error>{formatMessage({ id: 'An error occurred while fetching the data.' })}</FormHelperText>}
        {!dateRange && (
          <FormHelperText error>{formatMessage({ id: 'Select a start and end date' })}</FormHelperText>
        )}

        <SummarySection
          totalOrganizations={reportData?.totalOrganizations ?? 0}
          totalActivities={reportData?.totalActivities ?? 0}
          totalIndividuals={reportData?.totalIndividuals ?? 0}
          totalItemsDistributed={reportData?.totalItemsDistributed ?? 0}
          previousTotalActivities={previousReportData?.totalActivities}
          previousTotalIndividuals={previousReportData?.totalIndividuals}
          previousTotalItemsDistributed={previousReportData?.totalItemsDistributed}
        />

        <ActivitiesSection
          isLoading={isLoading}
          totalActivities={reportData?.totalActivities ?? 0}
          activitiesByType={reportData?.activitiesByType ?? []}
          activitiesByTheme={reportData?.activitiesByTheme ?? []}
          previousActivitiesByType={previousReportData?.activitiesByType}
          previousActivitiesByTheme={previousReportData?.activitiesByTheme}
          renderEmptyState={renderEmptyState}
        />

        <IndividualsSection
          isLoading={isLoading}
          totalIndividuals={reportData?.totalIndividuals ?? 0}
          individualsByAgeGroup={reportData?.individualsByAgeGroup ?? []}
          previousIndividualsByAgeGroup={previousReportData?.individualsByAgeGroup}
          renderEmptyState={renderEmptyState}
          formatAgeGroupLabel={formatAgeGroupLabel(formatMessage)}
        />
      </Stack>
    </>
  );
};
